/**
 * UOC Token Service Module
 *
 * Manages UOC token operations: mint, burn, and supply.
 */

import { api } from './client';
import type {
  UocSupply,
  UocMintRequest,
  UocBurnRequest,
  UocTransactionResponse,
  UocConfig,
  ListResponse,
  PaginationParams,
} from './types';

/**
 * UOC API
 *
 * Endpoints for UOC token management.
 */
export const uocAPI = {
  /**
   * Get UOC configurations
   *
   * Retrieves list of UOC token configurations.
   *
   * **Endpoint:** GET /api/uoc/configs
   * **Protected:** No
   */
  getConfigs: async (params?: PaginationParams): Promise<ListResponse<UocConfig>> =>
    api.get<ListResponse<UocConfig>>('/api/uoc/configs', params),

  /**
   * Get UOC supply information
   *
   * Retrieves current UOC supply details.
   *
   * **Endpoint:** GET /api/uoc/supply
   * **Protected:** No
   */
  getSupply: async (): Promise<UocSupply> => api.get<UocSupply>('/api/uoc/supply'),

  /**
   * Mint UOC tokens
   *
   * Mints new UOC tokens.
   *
   * **Endpoint:** POST /api/uoc/mint
   * **Protected:** Yes
   */
  mint: async (request: UocMintRequest): Promise<UocTransactionResponse> =>
    api.post<UocTransactionResponse>('/api/uoc/mint', request),

  /**
   * Burn UOC tokens
   *
   * Burns (destroys) UOC tokens.
   *
   * **Endpoint:** POST /api/uoc/burn
   * **Protected:** Yes
   */
  burn: async (request: UocBurnRequest): Promise<UocTransactionResponse> =>
    api.post<UocTransactionResponse>('/api/uoc/burn', request),

  transferOut: (body: {
    to_username: string;
    amount_uoc: string;
    memo?: string;
    transaction_password: string;
    security_answer: string;
  }) => api.post<Record<string, unknown>>('/api/uoc/transfer-out', body),

  transferOutMine: (params?: PaginationParams) =>
    api.get<ListResponse<Record<string, unknown>>>('/api/uoc/transfer-out/mine', params),
};
